import { autoinject, newInstance } from 'aurelia-framework';
import { Router } from "aurelia-router";
import { applicationLogger } from "../../logging/logger";
import { AdminApi } from "../../services/admin-api";
import { OpenIdConnect } from "aurelia-open-id-connect";
import { Tenant, TenantSetting } from "../../models/tenant";
import { TenantService } from "../../services/tenant-service";
import { MaterializeFormValidationRenderer, MdToastService, MdModal } from "aurelia-materialize-bridge";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { Page } from "../common/abstract-page";
import { DataProcessorSettings } from "../../models/data-processor-settings";

@autoinject
export class Tenants extends Page {

    validationRules: ValidationRules;
    tenants: Array<Tenant>;
    dataProcessorSettings: DataProcessorSettings;
    addTenantModal: MdModal;
    changeProcessingModal: MdModal;
    tenantSettingsModal: MdModal;
    globalSettingsModal: MdModal;
    newTenant: Tenant;
    selectedTenant: Tenant;
  
    filters = [
        { value: '', keys: ['name'] }
    ];
    constructor(adminApi: AdminApi,
        toast: MdToastService,
        router: Router,
        openIdConnect: OpenIdConnect,
        @newInstance(ValidationController) private validationController: ValidationController,
        private tenantService: TenantService) {

        super(adminApi, toast, router, openIdConnect);

        this.validationController.addRenderer(new MaterializeFormValidationRenderer());

        this.validationRules = ValidationRules
            .ensure("name")
            .required()
            .ensure("subDomain")
            .required()
            .on(Tenant)
            .rules;
    }

    async activate() {
        this.tenantService.tenantId = "";
        this.tenantService.tenantName = "";

        await this.getTenants();
        await this.getDataProcessorSettings();
    }

    async getTenants() {
        const response = await this.adminApi.getTenants();
        if (response.success && response.data) {
            this.tenants = response.data as Array<Tenant>;

            // TODO:  This is a temporary fix to redirect to the transactions page if the user is a tenant administrator and there is only one tenant
            //if (this.applicationUserIsTenantAdministratorOnly && this.tenants.length === 1) {
            //    this.router.navigateToRoute("transactions", { tenantId: this.tenants[0].id });
            //}
        } else {
            applicationLogger.error(response.message);
        }
    }

    async getDataProcessorSettings() {
        const response = await this.adminApi.getDataProcessorSettings();
        if (response.success && response.data) {
            this.dataProcessorSettings = response.data;
        } else {
            applicationLogger.error(response.message);
        }
    }

    showGlobalSettings() {

    }

    manageTransactions(id, name) {
        this.tenantService.tenantId = id;
        this.tenantService.tenantName = name;
        this.router.navigateToRoute("transactions", { tenantId: id });
    }

    navigateToReports(tenantId, tenantName) {
        this.tenantService.tenantId = tenantId;
        this.tenantService.tenantName = tenantName;
        this.router.navigateToRoute("reports", { tenantId: tenantId });
    }

    manageKiosks(tenantId, tenantName) {
        this.tenantService.tenantId = tenantId;
        this.tenantService.tenantName = tenantName;
        this.router.navigateToRoute("kiosks", { tenantId: tenantId });
    }

    manageUsers(tenantId, tenantName) {
        this.tenantService.tenantId = tenantId;
        this.tenantService.tenantName = tenantName;
        this.router.navigateToRoute("users", { tenantId: tenantId });
    }

    async openAddTenantModal() {
        this.newTenant = new Tenant();
        //this.newTenant.settings = new Array<TenantSetting>();
        //this.newTenant.settings.push(new TenantSetting("EnqueueTransactions", "False"));
        this.addTenantModal.open();
    }

    updateSubdomain() {
        this.newTenant.subDomain = this.newTenant.name.toLowerCase().replace(/\s+/g, '');
    }

    async addTenant() {
        this.validationController.validate().then(async v => {
            if (v.valid) {
                this.newTenant.containerName = this.newTenant.subDomain;
                const response = await this.adminApi.addTenant(this.newTenant);
                if (response.success) {
                    this.toast.show(response.message);
                    const response2 = await this.adminApi.enableTransactionProcessing(response.data.id, false);
                    if (response2.success) {
                        this.toast.show(response2.message);
                    } else {
                        this.toast.show(response2.message, 5000, "red");
                    }
                } else {
                    this.toast.show(response.message, 5000, "red");
                }
                this.addTenantModal.close();
                await this.getTenants();
            }
        });
    }

    async cancelAddTenant() {
        this.addTenantModal.close();
    }

    async openChangeProcessingModal(id: string) {
        this.selectedTenant = (this.tenants.find((tenant): boolean => tenant.id === id)) as Tenant;
        this.changeProcessingModal.open();
    }

    async changeProcessing(enabled: boolean) {
        const response = await this.adminApi.enableTransactionProcessing(this.selectedTenant.id, enabled);
        if (response.success) {
            this.toast.show(response.message);
        } else {
            this.toast.show(response.message, 5000, "red");
        }
        this.changeProcessingModal.close();

        await this.getTenants();
    }

    async cancelChangeProcessing() {
        this.changeProcessingModal.close();
    }

    async openTenantSettingsModal(id: string) {
        this.selectedTenant = (this.tenants.find((tenant): boolean => tenant.id === id)) as Tenant;
        this.tenantSettingsModal.open();
    }

    async updateSettings() {
        this.tenantSettingsModal.close();
    }

    async cancelUpdateSettings() {
        this.tenantSettingsModal.close();
    }

    async openGlobalSettingsModal() {
        this.globalSettingsModal.open();
    }

    async updateGlobalSettings() {
        this.globalSettingsModal.close();
    }

    async cancelGlobalSettings() {
        this.globalSettingsModal.close();
    }
}